/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * 31 Milk St # 960789 Boston, MA 02196 USA.
 *
 * Copyright (c) 2021 - 2025 (original work) Open Assessment Technologies SA
 */

import { endpoints } from '@/core/utils/consts';
import request from 'tao-core-sdk/core/fetchRequest';
import { compile } from 'path-to-regexp';
import { setAccessToken, refreshAccessToken } from '@/core/accessToken/accessToken';
import env from '@/config/env';

/**
 * Gets requested endpoint url replacing variables (if present) and appending parameters (if present)
 * @param {Object} urlData
 * @param {String} urlData.domain - key of the env variable containing the target domain
 * @param {String} urlData.endpoint - key config file containing the target BE endpoint
 * @param {Object} variables - object {variableName: variableValue, ...}. variableName must
 *  match a variable on retrieved endpoint from consts file eg.: {id: 1} for endpoint 'init/:id'
 * @param {Object} parameters - key/value GET params to append to url
 * @returns {String} - Built url ready to request to server
 */
export const getEndpointUrl = (urlData, variables = {}, parameters = {}) => {
    // get endpoint from consts file
    let endpoint = endpoints[urlData.endpoint];

    // replace endpoint variables
    endpoint = compile(endpoint)(variables);

    // extend api url for shared domain services
    const parsedDomainUrl = new URL(env(urlData.domain));
    if (parsedDomainUrl.pathname !== '' && parsedDomainUrl.pathname !== '/'){
        endpoint = `${parsedDomainUrl.pathname}${endpoint}`;
    }

    // build url appending endpoint to base url
    const url = new URL(endpoint, env(urlData.domain));

    // append query parameters to url
    Object.keys(parameters).forEach(parameterName => {
        url.searchParams.append(parameterName, parameters[parameterName]);
    });

    return url.href;
};

/**
 * Execs fetchRequest from tao-core-sdk-fe, adding jwtTokenHandler to received options
 * @param {URL} url - URL to fetch
 * @param {Object} options - objects to append to fetch
 * @returns {Promise} promise returned by fetchRequest to handle HTTP request response
 */
export default (url, options = {}) => {
    const jwtTokenHandler = {
        getToken: () => setAccessToken(),
        refreshToken: refreshAccessToken
    };
    // return request(url, Object.assign({}, options, { jwtTokenHandler }));
    return new Promise((resolve, reject) => {
        request(url, Object.assign({}, options, { jwtTokenHandler }))
            .then(response => resolve(response))
            .catch(err => {
                reject(err);
            });
    });
};
